/**
 * Video Editor Reducer — all state transitions for the SRMD Video Edit Suite
 */

export const ActionTypes = {
    // Video meta
    SET_VIDEO:              'SET_VIDEO',
    SET_DURATION:           'SET_DURATION',

    // Playback
    SET_PLAYHEAD:           'SET_PLAYHEAD',
    PLAY:                   'PLAY',
    PAUSE:                  'PAUSE',
    SET_PREVIEW_LANG:       'SET_PREVIEW_LANG',
    TOGGLE_SUBS:            'TOGGLE_SUBS',

    // Timeline / tool
    SET_TOOL:               'SET_TOOL',
    SET_ZOOM:               'SET_ZOOM',

    // Cuts
    ADD_CUT_MARKER:         'ADD_CUT_MARKER',
    REMOVE_CUT_MARKER:      'REMOVE_CUT_MARKER',
    CLEAR_CUT_MARKERS:           'CLEAR_CUT_MARKERS',
    SELECT_SEGMENT:              'SELECT_SEGMENT',
    RIPPLE_DELETE_SEGMENT:       'RIPPLE_DELETE_SEGMENT',
    ADD_CUT_MARKER_WITH_SPLIT:   'ADD_CUT_MARKER_WITH_SPLIT',

    // Subtitles
    LOAD_SUBTITLES:         'LOAD_SUBTITLES',
    MOVE_SUBTITLE_CUES:     'MOVE_SUBTITLE_CUES',  // { language, cueId, deltaSeconds, ripple }

    // Audio tracks
    LOAD_DUB_TRACKS:        'LOAD_DUB_TRACKS',

    // Audio tracks (BGM, dubbing, etc.)
    ADD_AUDIO_TRACK:        'ADD_AUDIO_TRACK',      // { id, name, url, volume, offsetSeconds }
    REMOVE_AUDIO_TRACK:     'REMOVE_AUDIO_TRACK',   // track id string
    UPDATE_AUDIO_TRACK:     'UPDATE_AUDIO_TRACK',   // { id, patch }
    PIN_AUDIO_VOLUME:       'PIN_AUDIO_VOLUME',      // track id — toggle pinVolume flag
    NORMALIZE_AUDIO:        'NORMALIZE_AUDIO',       // scale unpinned tracks to a balanced level
    // Video audio
    TOGGLE_MUTE_VIDEO:      'TOGGLE_MUTE_VIDEO',
    SET_VIDEO_VOLUME:       'SET_VIDEO_VOLUME',
    SET_OVERLAY_AUDIO:      'SET_OVERLAY_AUDIO',

    // Logo overlay tracks (supports multiple images, each with its own time range)
    ADD_LOGO_TRACK:         'ADD_LOGO_TRACK',    // { url, name, file?, position, opacity, startTime, endTime, labelRole }
    REMOVE_LOGO_TRACK:      'REMOVE_LOGO_TRACK', // track id string
    UPDATE_LOGO_TRACK:      'UPDATE_LOGO_TRACK', // { id, patch }

    // Pre-roll / post-roll offset (shifts video + subtitle tracks right on timeline)
    SET_VIDEO_OFFSET:       'SET_VIDEO_OFFSET',  // number (seconds) — intro pre-roll
    SET_VIDEO_END_OFFSET:   'SET_VIDEO_END_OFFSET', // number (seconds) — outro post-roll

    // DubSync tracks (embedded DubSync workflow within the editor)
    ADD_DUBSYNC_TRACK:      'ADD_DUBSYNC_TRACK',    // { language, hasDubbedAudio }
    UPDATE_DUBSYNC_TRACK:   'UPDATE_DUBSYNC_TRACK', // { language, patch }
    REMOVE_DUBSYNC_TRACK:   'REMOVE_DUBSYNC_TRACK', // language string
    SET_DUBSYNC_CUES:       'SET_DUBSYNC_CUES',     // { language, dubSyncCues, originalCues, rawWhisperCues, problematicCues, perCueRatios }
    UPDATE_DUBSYNC_CUES:    'UPDATE_DUBSYNC_CUES',  // { language, cues: [{id, startTime, endTime, text}] } — user-edited cues

    // UI panel
    TOGGLE_MEDIA_LIBRARY:   'TOGGLE_MEDIA_LIBRARY',

    // History
    UNDO:                   'UNDO',
    REDO:                   'REDO',

    // Persistence
    RESTORE_EDITOR_STATE:   'RESTORE_EDITOR_STATE', // { cutMarkers, cuts, music, logoTracks }
    MARK_SAVED:             'MARK_SAVED',            // records current past.length as the saved baseline
};

export const Tools = {
    SELECT: 'select',
    RAZOR:  'razor',
    HAND:   'hand',
};

/**
 * The timeline is divided into segments by cut markers.
 * A segment is "deleted" when the user selects it and presses Delete.
 * Deleted segments are stored in `cuts` (sorted by startTime).
 *
 * State shape:
 * {
 *   videoId, videoUrl, videoTitle, videoData,
 *   duration,                   // original video seconds
 *   playheadTime,               // in OUTPUT timeline (after cuts collapsed)
 *   isPlaying,
 *   previewLang,                // language shown in program monitor
 *   previewSubsOn,
 *
 *   // --- edit state (pushed to undo stack on each mutation) ---
 *   cutMarkers: [number],       // sorted list of cut points on original timeline (seconds)
 *   cuts: [{ id, startTime, endTime }],  // ripple-deleted segments (original time)
 *   subtitlesByLanguage: { [lang]: [{ id, startTime, endTime, text }] },
 *   dubTracks: [{ id, language, url, label }],
 *
 *   // --- ui ---
 *   activeTool,
 *   zoomLevel,                  // pixels per second
 *   selectedSegment,            // { startTime, endTime } or null
 *
 *   // --- undo / redo ---
 *   past: [],
 *   future: [],
 * }
 */

const SNAPSHOTTED_KEYS = ['cutMarkers', 'cuts', 'subtitlesByLanguage', 'audioTracks', 'logoTracks'];

function snapshot(state) {
    const snap = {};
    SNAPSHOTTED_KEYS.forEach((k) => (snap[k] = state[k]));
    return snap;
}

function applySnapshot(state, snap) {
    return { ...state, ...snap };
}

export const initialState = {
    videoId: null,
    videoUrl: null,
    videoTitle: '',
    videoData: null,
    duration: 0,

    playheadTime: 0,
    isPlaying: false,
    previewLang: 'English',
    previewSubsOn: true,

    cutMarkers: [],    // sorted seconds on original timeline
    cuts: [],          // { id, startTime, endTime }
    subtitlesByLanguage: {},
    dubTracks: [],

    // DubSync tracks — one entry per base language the user has enabled DubSync for
    // Shape: [{ language, trackState, uploadedAudioFile, originalCues, dubSyncCues,
    //           problematicCues, perCueRatios, renderedAudioUrl, error }]
    // trackState: 'NO_AUDIO'|'READY'|'SYNCING'|'SYNCED'|'RENDERING'|'RENDERED'|'FAILED'
    dubSyncTracks: [],

    // Audio tracks (BGM, dubbing, etc.)
    audioTracks: [],   // [{ id, name, url, volume (0-1), offsetSeconds, _audioDuration? }]
    muteVideoAudio: false,
    // Preview video element volume (0-1)
    videoVolume: 1,
    // Whether overlay (intro/outro) videos should play their own audio in preview
    playOverlayAudio: false,

    // Logo overlay tracks — each image can span the full video (watermark) or just a section (intro/outro)
    // Times are in OUTPUT-TIME space (0 = start of timeline, videoStartOffset = where video begins)
    logoTracks: [],    // [{ id, url, name, file?, position, opacity (0-1), startTime, endTime, labelRole ('Intro'|'Outro'|'Watermark'|'Custom') }]

    // Pre-roll offset: video and subtitle tracks are shifted right by this many seconds
    // Intro images occupy [0, videoStartOffset); video content starts at videoStartOffset
    videoStartOffset: 0,
    // Post-roll offset: timeline extends by this after the video content
    // Outro images occupy [videoStartOffset+duration, videoStartOffset+duration+videoEndOffset)
    videoEndOffset: 0,

    // UI
    mediaLibraryOpen: true,
    activeTool: Tools.SELECT,
    zoomLevel: 50,     // px/sec
    selectedSegment: null,

    past: [],
    future: [],
    savedPastLength: 0, // past.length at the time of last successful save
};

let cutIdCounter = 1;

export function editorReducer(state, action) {
    switch (action.type) {

        case ActionTypes.SET_VIDEO:
            return {
                ...initialState,
                videoId: action.payload.videoId,
                videoUrl: action.payload.videoUrl,
                videoTitle: action.payload.videoTitle,
                videoData: action.payload.videoData,
            };

        case ActionTypes.SET_DURATION:
            return { ...state, duration: action.payload };

        case ActionTypes.SET_PLAYHEAD:
            return { ...state, playheadTime: action.payload };

        case ActionTypes.PLAY:
            return { ...state, isPlaying: true };

        case ActionTypes.PAUSE:
            return { ...state, isPlaying: false };

        case ActionTypes.SET_PREVIEW_LANG:
            return { ...state, previewLang: action.payload };

        case ActionTypes.TOGGLE_SUBS:
            return { ...state, previewSubsOn: !state.previewSubsOn };

        case ActionTypes.SET_TOOL:
            return { ...state, activeTool: action.payload, selectedSegment: null };

        case ActionTypes.SET_ZOOM:
            return { ...state, zoomLevel: Math.max(10, Math.min(500, action.payload)) };

        case ActionTypes.ADD_CUT_MARKER: {
            const t = action.payload; // seconds on original timeline
            if (state.cutMarkers.includes(t)) return state;
            const updated = [...state.cutMarkers, t].sort((a, b) => a - b);
            return { ...state, cutMarkers: updated };
        }

        case ActionTypes.REMOVE_CUT_MARKER: {
            return { ...state, cutMarkers: state.cutMarkers.filter((m) => m !== action.payload) };
        }

        case ActionTypes.CLEAR_CUT_MARKERS:
            return { ...state, cutMarkers: [], selectedSegment: null };

        case ActionTypes.SELECT_SEGMENT:
            return { ...state, selectedSegment: action.payload }; // { startTime, endTime }

        case ActionTypes.RIPPLE_DELETE_SEGMENT: {
            const { startTime, endTime, affectedLanguages, affectMusic, affectLogo } = action.payload;

            // push to undo stack
            const past = [...state.past, snapshot(state)];
            const future = [];

            const cutDuration = endTime - startTime;
            const newCutId = `cut_${cutIdCounter++}`;

            // Remove this segment's markers from the list
            const newMarkers = state.cutMarkers.filter((m) => m < startTime || m > endTime);

            // Add new cut entry
            const newCuts = [...state.cuts, { id: newCutId, startTime, endTime }].sort(
                (a, b) => a.startTime - b.startTime,
            );

            // Determine which languages to adjust; default to all for backwards compat
            const langsToAdjust = affectedLanguages != null
                ? new Set(affectedLanguages)
                : new Set(Object.keys(state.subtitlesByLanguage));

            // Adjust subtitles for selected languages only
            const newSubsByLang = {};
            Object.entries(state.subtitlesByLanguage).forEach(([lang, cues]) => {
                newSubsByLang[lang] = langsToAdjust.has(lang)
                    ? adjustSubtitlesForCut(cues, startTime, endTime, cutDuration)
                    : cues;
            });

            // Optionally adjust audio track offsets
            const newAudioTracks = affectMusic
                ? state.audioTracks.map((t) => adjustMusicForCut(t, startTime, endTime, cutDuration))
                : state.audioTracks;

            // Optionally adjust logo track timings (logo times are in output-time; convert cut to output-time)
            const newLogoTracks = affectLogo && state.logoTracks.length
                ? state.logoTracks.map((lt) => adjustLogoForCut(lt, startTime + state.videoStartOffset, endTime + state.videoStartOffset, cutDuration))
                : state.logoTracks;

            return {
                ...state,
                cutMarkers: newMarkers,
                cuts: newCuts,
                subtitlesByLanguage: newSubsByLang,
                audioTracks: newAudioTracks,
                logoTracks: newLogoTracks,
                selectedSegment: null,
                past,
                future,
            };
        }

        case ActionTypes.ADD_CUT_MARKER_WITH_SPLIT: {
            const { time, affectedLanguages: splitLangs } = action.payload;
            if (state.cutMarkers.includes(time)) return state;

            const updatedMarkers = [...state.cutMarkers, time].sort((a, b) => a - b);

            const langsToSplit = splitLangs != null
                ? new Set(splitLangs)
                : new Set(Object.keys(state.subtitlesByLanguage));

            let cuesModified = false;
            const splitSubsByLang = {};
            Object.entries(state.subtitlesByLanguage).forEach(([lang, cues]) => {
                if (langsToSplit.has(lang)) {
                    const next = splitCuesAtTime(cues, time);
                    if (next !== cues) cuesModified = true;
                    splitSubsByLang[lang] = next;
                } else {
                    splitSubsByLang[lang] = cues;
                }
            });

            // Only push undo snapshot when subtitle content actually changed
            const past = cuesModified ? [...state.past, snapshot(state)] : state.past;
            const future = cuesModified ? [] : state.future;

            return {
                ...state,
                cutMarkers: updatedMarkers,
                subtitlesByLanguage: splitSubsByLang,
                past,
                future,
            };
        }

        case ActionTypes.LOAD_SUBTITLES:
            return { ...state, subtitlesByLanguage: action.payload };

        case ActionTypes.MOVE_SUBTITLE_CUES: {
            const { language, cueId, deltaSeconds, ripple } = action.payload;
            const cues = state.subtitlesByLanguage[language];
            if (!cues) return state;
            const anchorIdx = cues.findIndex((c) => c.id === cueId);
            if (anchorIdx === -1) return state;
            const anchorStart = cues[anchorIdx].startTime;
            const newCues = cues.map((c, i) => {
                const shouldMove = i === anchorIdx || (ripple && c.startTime >= anchorStart && i > anchorIdx);
                if (!shouldMove) return c;
                return {
                    ...c,
                    startTime: Math.max(0, c.startTime + deltaSeconds),
                    endTime: Math.max(0, c.endTime + deltaSeconds),
                };
            });
            return {
                ...state,
                subtitlesByLanguage: { ...state.subtitlesByLanguage, [language]: newCues },
                past: [...state.past, snapshot(state)],
                future: [],
            };
        }

        case ActionTypes.LOAD_DUB_TRACKS:
            return { ...state, dubTracks: action.payload };

        case ActionTypes.ADD_AUDIO_TRACK:
            // Idempotent: ignore if a track with the same id already exists
            if (state.audioTracks.some((t) => t.id === action.payload?.id)) return state;
            return { ...state, audioTracks: [...state.audioTracks, action.payload] };

        case ActionTypes.REMOVE_AUDIO_TRACK:
            return { ...state, audioTracks: state.audioTracks.filter((t) => t.id !== action.payload) };

        case ActionTypes.UPDATE_AUDIO_TRACK: {
            const { id, patch } = action.payload;
            return { ...state, audioTracks: state.audioTracks.map((t) => (t.id === id ? { ...t, ...patch } : t)) };
        }

        case ActionTypes.PIN_AUDIO_VOLUME:
            return {
                ...state,
                audioTracks: state.audioTracks.map((t) =>
                    t.id === action.payload ? { ...t, pinVolume: !t.pinVolume } : t,
                ),
            };

        case ActionTypes.NORMALIZE_AUDIO: {
            const NORMALIZE_TARGET = 0.72; // broadcast-safe neutral level
            const unpinned = state.audioTracks.filter((t) => !t.pinVolume);
            if (unpinned.length === 0) return state;
            const avg = unpinned.reduce((sum, t) => sum + t.volume, 0) / unpinned.length;
            const ratio = avg > 0 ? NORMALIZE_TARGET / avg : 1;
            return {
                ...state,
                audioTracks: state.audioTracks.map((t) => {
                    if (t.pinVolume) return t;
                    const newVol = parseFloat(Math.max(0.05, Math.min(1, t.volume * ratio)).toFixed(2));
                    return { ...t, volume: newVol };
                }),
            };
        }

        case ActionTypes.TOGGLE_MUTE_VIDEO:
            return { ...state, muteVideoAudio: !state.muteVideoAudio };

        case ActionTypes.SET_VIDEO_VOLUME:
            return { ...state, videoVolume: Math.max(0, Math.min(1, parseFloat(action.payload))) };

        case ActionTypes.SET_OVERLAY_AUDIO:
            return { ...state, playOverlayAudio: !!action.payload };

        case ActionTypes.ADD_LOGO_TRACK: {
            const lt = {
                position: 'bottom-right',
                opacity: 1,
                scale: 0.2,
                startTime: 0,
                endTime: state.duration,
                labelRole: 'Watermark',
                ...action.payload,
                id: action.payload.id || `logo_${Date.now()}`,
            };
            return { ...state, logoTracks: [...state.logoTracks, lt] };
        }

        case ActionTypes.REMOVE_LOGO_TRACK:
            return { ...state, logoTracks: state.logoTracks.filter((lt) => lt.id !== action.payload) };

        case ActionTypes.UPDATE_LOGO_TRACK: {
            const { id, patch } = action.payload;
            return { ...state, logoTracks: state.logoTracks.map((lt) => (lt.id === id ? { ...lt, ...patch } : lt)) };
        }

        case ActionTypes.SET_VIDEO_OFFSET:
            return { ...state, videoStartOffset: Math.max(0, action.payload) };

        case ActionTypes.SET_VIDEO_END_OFFSET:
            return { ...state, videoEndOffset: Math.max(0, action.payload) };

        case ActionTypes.TOGGLE_MEDIA_LIBRARY:
            return { ...state, mediaLibraryOpen: !state.mediaLibraryOpen };

        // ── DubSync tracks ────────────────────────────────────────────────
        case ActionTypes.ADD_DUBSYNC_TRACK: {
            const { language, hasDubbedAudio } = action.payload;
            if (state.dubSyncTracks.some((t) => t.language === language)) return state;
            const newTrack = {
                language,
                trackState: hasDubbedAudio ? 'READY' : 'NO_AUDIO',
                uploadedAudioFile: null,
                originalCues: [],
                dubSyncCues: [],
                problematicCues: [],
                perCueRatios: [],
                renderedAudioUrl: null,
                error: null,
            };
            return { ...state, dubSyncTracks: [...state.dubSyncTracks, newTrack] };
        }

        case ActionTypes.UPDATE_DUBSYNC_TRACK: {
            const { language, patch } = action.payload;
            return {
                ...state,
                dubSyncTracks: state.dubSyncTracks.map((t) => {
                    if (t.language !== language) return t;
                    const merged = { ...t, ...patch };
                    // If a file was attached to the track and the track previously had no audio,
                    // mark it READY so Sync Magic becomes available. Do not override active/finished states.
                    if (patch.uploadedAudioFile && (t.trackState === 'NO_AUDIO' || !t.trackState)) {
                        merged.trackState = 'READY';
                    }
                    return merged;
                }),
            };
        }

        case ActionTypes.REMOVE_DUBSYNC_TRACK: {
            const lang = action.payload;
            const dubSyncLangKey = `${lang}-DubSync`;
            const { [dubSyncLangKey]: _removed, ...remainingSubs } = state.subtitlesByLanguage;
            return {
                ...state,
                dubSyncTracks: state.dubSyncTracks.filter((t) => t.language !== lang),
                subtitlesByLanguage: remainingSubs,
            };
        }

        case ActionTypes.SET_DUBSYNC_CUES: {
            const { language, dubSyncCues, originalCues, rawWhisperCues, problematicCues, perCueRatios } = action.payload;
            const dubSyncLangKey = `${language}-DubSync`;
            const whisperLangKey = `${language}-Whisper`;
            // Map to timeline format — accept both {start,end} (raw API) and {startTime,endTime} (normalized)
            const mapCue = (c) => ({ id: c.id, startTime: c.startTime ?? c.start, endTime: c.endTime ?? c.end, text: c.text });
            const subtitleCues = dubSyncCues.map(mapCue);
            const whisperCues  = (rawWhisperCues || []).map(mapCue);
            return {
                ...state,
                subtitlesByLanguage: {
                    ...state.subtitlesByLanguage,
                    [dubSyncLangKey]: subtitleCues,
                    [whisperLangKey]: whisperCues,
                },
                dubSyncTracks: state.dubSyncTracks.map((t) =>
                    t.language === language
                        ? { ...t, dubSyncCues, originalCues, problematicCues: problematicCues || [], perCueRatios: perCueRatios || [], trackState: 'SYNCED', error: null }
                        : t,
                ),
            };
        }

        case ActionTypes.UPDATE_DUBSYNC_CUES: {
            const { language: updLang, cues: updCues } = action.payload;
            const updKey = `${updLang}-DubSync`;
            return {
                ...state,
                subtitlesByLanguage: {
                    ...state.subtitlesByLanguage,
                    [updKey]: updCues,
                },
                dubSyncTracks: state.dubSyncTracks.map((t) =>
                    t.language === updLang
                        ? { ...t, trackState: 'SYNCED', error: null, problematicCues: [] }
                        : t,
                ),
            };
        }

        case ActionTypes.UNDO: {
            if (state.past.length === 0) return state;
            const previous = state.past[state.past.length - 1];
            const newPast = state.past.slice(0, -1);
            const future = [snapshot(state), ...state.future];
            return applySnapshot({ ...state, past: newPast, future }, previous);
        }

        case ActionTypes.REDO: {
            if (state.future.length === 0) return state;
            const next = state.future[0];
            const newFuture = state.future.slice(1);
            const past = [...state.past, snapshot(state)];
            return applySnapshot({ ...state, past, future: newFuture }, next);
        }

        case ActionTypes.RESTORE_EDITOR_STATE: {
            const { cutMarkers, cuts, audioTracks, muteVideoAudio, logoTracks, videoStartOffset, videoEndOffset } = action.payload;
            // Strip DubSync raw audio tracks — they are session-only and must not be restored from saved state
            const restoredAudioTracks = Array.isArray(audioTracks)
                ? audioTracks.filter((t) => !t.id?.startsWith('dubsync-raw-'))
                : state.audioTracks;
            return {
                ...state,
                cutMarkers: Array.isArray(cutMarkers) ? cutMarkers : state.cutMarkers,
                cuts: Array.isArray(cuts) ? cuts : state.cuts,
                audioTracks: restoredAudioTracks,
                muteVideoAudio: muteVideoAudio !== undefined ? muteVideoAudio : state.muteVideoAudio,
                logoTracks: Array.isArray(logoTracks) ? logoTracks : state.logoTracks,
                videoStartOffset: videoStartOffset !== undefined ? videoStartOffset : state.videoStartOffset,
                videoEndOffset: videoEndOffset !== undefined ? videoEndOffset : state.videoEndOffset,
                past: [],
                future: [],
                savedPastLength: 0,
            };
        }

        case ActionTypes.MARK_SAVED:
            return { ...state, savedPastLength: state.past.length };

        default:
            return state;
    }
}

/**
 * Split any subtitle cue that spans `time` into two cues at that point.
 * Cues that don't span `time` are left unchanged.
 * Returns the same array reference if nothing was split.
 */
function splitCuesAtTime(cues, time) {
    let changed = false;
    const result = [];
    for (const cue of cues) {
        if (cue.startTime < time && cue.endTime > time) {
            changed = true;
            result.push({ ...cue, endTime: time });
            result.push({ ...cue, id: `${cue.id}_s${Date.now()}`, startTime: time });
        } else {
            result.push(cue);
        }
    }
    return changed ? result : cues;
}

/**
 * Adjust music BGM offsetSeconds after a ripple delete of [startTime, endTime].
 */
function adjustMusicForCut(music, startTime, endTime, cutDuration) {
    const off = music.offsetSeconds;
    if (off >= endTime) {
        return { ...music, offsetSeconds: off - cutDuration };
    }
    if (off > startTime) {
        return { ...music, offsetSeconds: startTime };
    }
    return music;
}

/**
 * Adjust logo startTime/endTime after a ripple delete of [startTime, endTime].
 * Applies the same boundary logic as adjustSubtitlesForCut.
 */
function adjustLogoForCut(logo, cutStart, cutEnd, cutDuration) {
    const { startTime: ls, endTime: le } = logo;

    // Entirely within cut → collapse
    if (ls >= cutStart && le <= cutEnd) {
        return { ...logo, startTime: cutStart, endTime: cutStart };
    }
    // Straddles end boundary (starts before, ends inside)
    if (ls < cutStart && le > cutStart && le <= cutEnd) {
        return { ...logo, endTime: cutStart };
    }
    // Straddles start boundary (starts inside, ends after)
    if (ls >= cutStart && ls < cutEnd && le > cutEnd) {
        return { ...logo, startTime: cutStart, endTime: le - cutDuration };
    }
    // Spans entire cut (starts before, ends after) → shrink
    if (ls < cutStart && le > cutEnd) {
        return { ...logo, endTime: le - cutDuration };
    }
    // Entirely after cut → shift left
    if (ls >= cutEnd) {
        return { ...logo, startTime: ls - cutDuration, endTime: le - cutDuration };
    }
    // Entirely before cut → unchanged
    return logo;
}

/**
 * Adjust subtitle cues after a ripple delete of [startTime, endTime].
 * - Cues entirely inside the cut range are removed.
 * - Cues that overlap the boundary are trimmed.
 * - All cues after endTime are shifted left by cutDuration.
 */
function adjustSubtitlesForCut(cues, startTime, endTime, cutDuration) {
    return cues
        .map((cue) => {
            // Entirely within cut → remove
            if (cue.startTime >= startTime && cue.endTime <= endTime) return null;

            // Straddles end boundary (starts before, ends inside)
            if (cue.startTime < startTime && cue.endTime > startTime && cue.endTime <= endTime) {
                return { ...cue, endTime: startTime };
            }

            // Straddles start boundary (starts inside, ends after)
            if (cue.startTime >= startTime && cue.startTime < endTime && cue.endTime > endTime) {
                return { ...cue, startTime: startTime, endTime: cue.endTime - cutDuration };
            }

            // Spans entire cut (start before, end after) → shrink
            if (cue.startTime < startTime && cue.endTime > endTime) {
                return { ...cue, endTime: cue.endTime - cutDuration };
            }

            // Entirely after cut → shift left
            if (cue.startTime >= endTime) {
                return { ...cue, startTime: cue.startTime - cutDuration, endTime: cue.endTime - cutDuration };
            }

            // Entirely before cut → unchanged
            return cue;
        })
        .filter(Boolean);
}
